import { PhoneNumber as MFAPhoneNumber } from "@/libs/apis/onboarding/mfa";
import useCurrentUserEmail from "@/src/services/auth/useCurrentUserEmail";
import { noop } from "lodash";
import { createContext, useContext, useEffect, useState } from "react";
import useMakeInitialVerificationCodeRequest from "../../hooks/useMakeInitialVerificationCodeRequest";
import { MFAType, SecureNumberState, VerificationMethod } from "./types";
import { useCurrentUser } from "@/libs/apis/onboarding/useUser";

const MFAContext = createContext<MFAType>({
  mfaState: SecureNumberState.PHONE_NUMBER,
  userHasNumber: false,
  phoneNumber: "",
  setPhoneNumber: noop,
  setMfaState: noop,
  setVerificationMethod: noop,
  reset: noop,
  verificationEntity: "",
  setEnabled: noop,
  setMfaId: noop,
  mfaId: "",
  verificationMethod: VerificationMethod.PHONE_NUMBER,
});

MFAContext.displayName = "MFA Status";

export const useMFAContext = () => {
  return useContext(MFAContext);
};

type Props = React.PropsWithChildren<{
  initialMFAState?: SecureNumberState;
  initialVerificationMethod?: VerificationMethod;
  initialEnabled?: boolean;
}>;

export const MFAProvider: React.FC<Props> = ({
  children,
  initialVerificationMethod = VerificationMethod.PHONE_NUMBER,
  initialMFAState = SecureNumberState.PHONE_NUMBER,
  initialEnabled = false,
}) => {
  const [mfaState, setMfaState] = useState<SecureNumberState>(initialMFAState);
  // used to disable/enable initial verification request so it does not trigger before modal opens
  const [enabled, setEnabled] = useState<boolean>(initialEnabled);
  const [mfaId, setMfaId] = useState<string>("");

  const { firstLoad, setFirstLoad, initialMfaId } =
    useMakeInitialVerificationCodeRequest(
      initialVerificationMethod,
      mfaState,
      enabled,
    );

  const [verificationMethod, setVerificationMethod] =
    useState<VerificationMethod>(initialVerificationMethod);

  const [phoneNumber, setPhoneNumber] = useState<MFAPhoneNumber>("");
  const { currentUser } = useCurrentUser();

  const currentUserEmail = useCurrentUserEmail();

  // we now use user's IDN profile phone number
  // (no longer collected during onboarding)
  const defaultPhoneNumber = currentUser?.contact?.sms?.verifiedNumber;
  const userHasNumber = !!defaultPhoneNumber;

  useEffect(() => {
    if (
      initialVerificationMethod === VerificationMethod.PHONE_NUMBER &&
      userHasNumber
    ) {
      setPhoneNumber(defaultPhoneNumber);
    }
  }, [userHasNumber, defaultPhoneNumber, initialVerificationMethod]);

  useEffect(() => {
    if (
      mfaState === SecureNumberState.PHONE_NUMBER &&
      userHasNumber &&
      firstLoad &&
      enabled
    ) {
      setMfaState(SecureNumberState.VERIFICATION);
    }
  }, [phoneNumber, mfaState, initialVerificationMethod, enabled, firstLoad]);

  useEffect(() => {
    if (initialMfaId) {
      setMfaId(initialMfaId);
    }
  }, [initialMfaId]);

  return (
    <MFAContext.Provider
      value={{
        userHasNumber,
        mfaState,
        verificationMethod,
        setVerificationMethod,
        setEnabled,
        reset: () => {
          setVerificationMethod(initialVerificationMethod);
          setMfaState(SecureNumberState.PHONE_NUMBER);
          setPhoneNumber("");
          setFirstLoad(true);
        },
        verificationEntity:
          verificationMethod === VerificationMethod.EMAIL && currentUserEmail
            ? currentUserEmail
            : phoneNumber,
        setMfaState,
        phoneNumber,
        setMfaId,
        mfaId,
        setPhoneNumber,
      }}
    >
      {children}
    </MFAContext.Provider>
  );
};

export default MFAContext;
